import { InputAdornment, TextField } from "@mui/material";
import { Col, Row, Form } from "react-bootstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import React, { useEffect, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useLoader } from "../../context/LoaderContext";
import {
  getMetaDataSelection,
  getProcessStages,
} from "../../api/columnHeaderSelection/columnHeaderSelectionApi";
import CustomSnackbar, {
  CustomNotificationHandle,
} from "../Notification/Notification";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { useNavigate } from "react-router-dom";
import LocalStorageUtil from "../../utils/localStorage";

function not(a: readonly number[], b: readonly number[]) {
  return a.filter((value) => !b.includes(value));
}

function intersection(a: readonly number[], b: any) {
  return b.filter((column: any) => a.includes(column.metadataItemId));
}

interface MetaDataFieldsProps {
  businessUnitData: any;
}

const MetaDataFields: React.FC<MetaDataFieldsProps> = ({
  businessUnitData,
}) => {
  const snackbarRef = useRef<CustomNotificationHandle>(null);
  const handleShowSnackbar = () => {
    snackbarRef.current?.showSnackbar("Data transfered successfully");
  };
  const [searchTermLeft, setSearchTerm] = useState("");// NOSONAR
  const [searchTermRight, setSearchTermRight] = useState("");
  const [history, setHistory] = useState<
    Array<{
      left: readonly number[];
      right: readonly number[];
      checked: readonly number[];
    }>
  >([]);
  const [data,] = useState<any>([]);
  const [checked, setChecked] = React.useState<readonly number[]>([]);
  const [left, setLeft] = useState<readonly number[]>([]);
  const [metaData, setMetaData] = useState<any[]>([]);
  const [right, setRight] = useState<any>([]);
  const [formData, setFormData] = useState({
    processStageName: data[0]?.processStageName || "",
  });

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);
  const { setLoading } = useLoader();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const navigate = useNavigate()

  const handleToggle = (value: number) => () => {
    saveToHistory();
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    saveToHistory();
    setLeft(left.concat(right));
    setRight([]);
  };

  const saveToHistory = () => {
    setHistory((prev) => [...prev, { left, right, checked }]);
  };

  const undoLastAction = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setLeft(lastState.left);
      setRight(lastState.right);
      setChecked(lastState.checked);
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  const customList = (items: readonly number[], searchTermLeft: string) => (
    <Paper sx={{ width: "100%", height: 490, overflow: "auto" }}>
      <List dense component="div" role="list">
        {items.length === 0 ? (
          <ListItemText sx={{ textAlign: "center", color: "gray" }}>
            No items available...
          </ListItemText>
        ) : (
          items
            .filter((item: any) =>
              item?.metadataItemName
                .toLowerCase()
                .includes(searchTermLeft.toLowerCase())
            )
            .map((value: any) => {
              const isSelected = checked.indexOf(value.metadataItemId) !== -1;
              return (
                <ListItemButton
                  key={value}
                  role="listitem"
                  onClick={handleToggle(value.metadataItemId)}
                  sx={{
                    backgroundColor: isSelected
                      ? " rgb(77, 123, 167)"
                      : "transparent",
                    border: isSelected ? "1px solid darkblue" : "",
                    color: isSelected ? "white" : "default",
                    "&:hover": {
                      backgroundColor: isSelected ? "darkblue" : "lightgray",
                    },
                  }}
                >
                  <ListItemText primary={value.metadataItemName} />
                </ListItemButton>
              );
            })
        )}
      </List>
    </Paper>
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchChangeRight = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTermRight(event.target.value);
  };

  const handleDropdownChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    const selectedProcessStage = metaData.find(
      (processStage: any) => processStage.processStageName === value
    );
    if (selectedProcessStage) {
      getAllMetaData(selectedProcessStage.processStageId);
    } else {
      getAllMetaData();
    }
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setFormData({
        processStageName: data[0]?.processStageName || "",
      });
    }
  }, [data]);

  const getAllMetaData = async (MetadataGroupId?: number) => {
    setLoading(true);
    const payload = {
      AccountId:
        businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      ...(MetadataGroupId !== undefined && {
        MetadataGroupId,
        UserId: LocalStorageUtil.getItem("userId"),
      }),
      contextName:
        businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getMetaDataSelection(payload);
      const rightItems = data.metaDataModel
        .filter((e: any) => e.metadataSelectedItemName != null)
        .map((e: any) => ({
          ...e,
          metadataItemName: e.metadataSelectedItemName,
        }));
      setRight(rightItems);
      const leftItems = data.metaDataModel.filter(
        (e: any) =>
          !rightItems.some(
            (r: any) => r.metadataItemName === e.metadataItemName
          )
      );
      setLeft(leftItems);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllProcessStages = async () => {
    setLoading(true);
    const payload = {
      AccountId:
        businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      contextName:
        businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getProcessStages(payload);
      setMetaData(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getAllProcessStages();
  }, [left, businessUnitData]);

  useEffect(() => {
    setFormData({ processStageName: "" });
    getAllMetaData();
  }, [businessUnitData]);

  const handleConfirmDialogClose = (confirm: boolean) => {
    setConfirmDialogOpen(false);
    if (confirm) {
      navigate("/");
    } else {
      setConfirmDialogOpen(false);
    }
  };

  const handelCancel = () => {
    setConfirmDialogOpen(true);
  };

  return (
   <div>
      <NavigationBar
        title="Metadata Fields"
        text="Create Metadata Fields Selection"
      />
      <div className="ag-theme-quartz-dark" style={{ marginLeft:"5vh" }}>
        <h4
          className="form-heading"
          style={{ color: "var(--aggrid-text-color)" }}
        >
          Metadata Fields Selection
        </h4>
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formCustomer">
                <Form.Label
                  className="input-labels"
                  style={{ color: "var(--aggrid-text-color)",fontSize: "11px" }}
                >
                  Queue Type<span className="required">*</span>
                </Form.Label>
                <Form.Select
                  name="processStageName"
                  value={formData.processStageName || ""}
                  onChange={handleDropdownChange}
                  style={{                                       
                    height: "30px",
                    fontSize: "11px",
                  }}
                >
                  <option value="">Select</option>
                  {metaData?.map((processStage: any) => (
                    <option
                      key={processStage.processStageId}
                      value={processStage.processStageName}
                    >
                      {processStage.processStageName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)", fontSize: "11px" }}
              >
                Available<span className="required">*</span>
              </Form.Label>
              <TextField
                fullWidth
                placeholder="Search"
                value={searchTermLeft}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  marginBottom: "10px",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    height: "35px",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#888",
                    },
                    "& input": {
        fontSize: "11px", 
      }
                  },
                }}
                variant="outlined"
              />
              <div>{customList(left, searchTermLeft)}</div>
            </Col>
            <Col
              md={1}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleAllRight}
                disabled={left.length === 0}
                aria-label="move all right"
                className="arrow-button"
              >
                ≫
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="move selected right"
                className="arrow-button"
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="move selected left"
                className="arrow-button"
              >
                &lt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleAllLeft}
                disabled={right.length === 0}
                aria-label="move all left"
                className="arrow-button"
              >
                ≪
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={undoLastAction}
                disabled={history.length === 0}
                aria-label="undo last action"
                className="arrow-button"
              >
                <RefreshIcon fontSize="small"
                  sx={{
                    fontSize: "14px",
                    height: "14px",
                    width: "14px",
                  }}/>
              </Button>
            </Col>
            <Col md={4}>
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)",fontSize: "11px" }}
              >
                Enabled<span className="required">*</span>
              </Form.Label>
              <TextField
                fullWidth
                placeholder="Search"
                value={searchTermRight}
                onChange={handleSearchChangeRight}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  "& .MuiOutlinedInput-root": {
                    height: "35px",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#888",
                    },
                    "& input": {
        fontSize: "11px", 
      }
                  },
                }}
                variant="outlined"
              />
              <div>{customList(right, searchTermRight)}</div>
            </Col>
          </Row>
          <Row>
            <Col
              md={9}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              <Button
                variant="contained"
                className="update-button"
                onClick={handleShowSnackbar}
              >
                SAVE
              </Button>
              <Button variant="contained" className="cancel-button" onClick={handelCancel}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
        <CustomSnackbar ref={snackbarRef} />
        <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        title={"Metadata Fields - Create Metadata Fields Selection"}
        content="Are you sure you want to Cancel?"
      />
      </div>
    </div>
  );
};

export default MetaDataFields;
